import { useState } from 'react';
import { DownloadIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { BundlePreview } from '@/lib/api/generated/model';

import { bundlePath, downloadBundle } from '../bundles/downloadBundle';
import { namedChoice, workflowTree } from '../bundles/includes';
import { useBundlePreview } from '../hooks/useBundlePreview';
import { useWorkflows } from '../hooks/useWorkflows';
import { FlowDialog, FlowDialogPending } from './FlowDialog';
import { IncludesChoice, IncludesLine, WorkflowTree } from './IncludesChoice';

/**
 * Take a workflow out as one file (spec 203; service spec 162).
 *
 * The file carries the workflow and every workflow it links to, so a tree moves in
 * one go — which is what the person could not do before. Before offering the
 * download, the dialog says exactly what will travel and what will not:
 * unpublished changes stay behind, and a draft anywhere below stops the export
 * with the service's own sentence, so the person never asks for a file that
 * would be refused.
 *
 * Agents and skills are named only inside prompts, so the ones the prompts
 * name travel without a tick, and one Includes line says so (spec 211);
 * Change holds the rest, a group at a time.
 */
export function ExportWorkflowDialog({
  workspaceId,
  workflow,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  workflow: { id: string; key: string; name: string };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { preview, isPending, loadFailed, refetch } = useBundlePreview(
    workspaceId,
    workflow.id,
    open,
  );
  // The person's ticks, for the preview they were made against. A new preview
  // (the dialog reopened, a child published since) starts again from the
  // service's suggestion rather than carrying stale ticks across.
  const [picked, setPicked] = useState<{
    source: BundlePreview;
    agents: Set<string>;
    skills: Set<string>;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  // The choice folds behind the Includes line: the usual export ticks nothing.
  const [changing, setChanging] = useState(false);
  const { workflows: views } = useWorkflows(workspaceId);
  const [failure, setFailure] = useState<string | null>(null);

  const choice =
    preview && picked?.source === preview
      ? picked
      : preview
        ? { source: preview, ...namedChoice(preview) }
        : null;
  const agents = choice?.agents ?? new Set<string>();
  const skills = choice?.skills ?? new Set<string>();

  const blocked = (preview?.blockers.length ?? 0) > 0;
  const behind = preview?.workflows.filter((w) => w.has_pending_changes) ?? [];

  const download = () => {
    setSaving(true);
    setFailure(null);
    downloadBundle(
      bundlePath(workspaceId, workflow.id, {
        agents: [...agents].sort(),
        skills: [...skills].sort(),
      }),
      `${workflow.key}.zip`,
    )
      .then(() => onOpenChange(false))
      .catch((error: unknown) =>
        setFailure(
          error instanceof Error
            ? error.message
            : 'The bundle could not be downloaded.',
        ),
      )
      .finally(() => setSaving(false));
  };

  return (
    <FlowDialog
      open={open}
      onOpenChange={onOpenChange}
      busy={saving}
      title={`Export ${workflow.name}`}
      description="Downloads one file with this workflow and every workflow it links to. Upload it in another workspace to bring the whole set in."
      alert={failure}
      primary={
        <Button onClick={download} disabled={!preview || blocked || saving}>
          <DownloadIcon aria-hidden="true" />
          {saving ? 'Preparing…' : 'Download'}
        </Button>
      }
    >
      {isPending && open ? (
        <FlowDialogPending label="Checking what travels…" />
      ) : loadFailed ? (
        <div className="flex flex-col gap-2">
          <p role="alert" className="text-destructive text-sm">
            What this export would hold could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="w-fit"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </div>
      ) : preview ? (
        <>
          {/* What travels, in the order every flow dialog says it (dialogs
              review 2): the Includes line, then the tree, then the choice. */}
          <IncludesLine
            preview={preview}
            choice={{ agents, skills }}
            open={changing}
            onToggle={() => setChanging(!changing)}
          />
          <WorkflowTree rows={workflowTree(preview.workflows, views)} />
          {behind.length > 0 && (
            <p className="text-muted-foreground -mt-2 text-xs">
              Unpublished changes to {behind.map((w) => w.name).join(', ')} are
              not included: only published versions travel.
            </p>
          )}
          {changing && (
            <IncludesChoice
              preview={preview}
              choice={{ agents, skills }}
              onChange={(next) => choice && setPicked({ ...choice, ...next })}
            />
          )}

          {blocked && (
            <div
              role="alert"
              className="border-destructive/40 bg-destructive/5 flex flex-col gap-1 rounded-lg border p-3"
            >
              <p className="text-sm font-medium">
                This workflow cannot be exported yet
              </p>
              <ul className="flex flex-col gap-0.5 text-xs">
                {preview.blockers.map((blocker) => (
                  <li key={blocker}>{blocker}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
    </FlowDialog>
  );
}
